// Canvas settings, presets and mobile-layout helpers shared by Builder & Viewer.

export const CANVAS_PRESETS = [
  { value: "fluid", label: "Fluid (infinite)", width: null, height: null },
  { value: "16:9", label: "16:9 Widescreen", width: 1600, height: 900 },
  { value: "4:3", label: "4:3 Classic", width: 1200, height: 900 },
  { value: "letter", label: "Letter (portrait)", width: 816, height: 1056 },
  { value: "custom", label: "Custom size", width: 1600, height: 1000 },
];

export const GRID_DENSITIES = [
  { value: 12, label: "Comfortable (12)" },
  { value: 24, label: "Fine (24)" },
  { value: 48, label: "Precise (48)" },
];

export const FIT_MODES = [
  { value: "fit_width", label: "Fit to width" },
  { value: "fit_page", label: "Fit whole page" },
  { value: "actual", label: "Actual size" },
];

// Legacy dashboards (no canvas saved) keep the exact old behavior.
export function normalizeCanvas(canvas) {
  const c = canvas || {};
  return {
    preset: c.preset || "fluid",
    width: c.width || presetSize(c.preset).width || 1600,
    height: c.height || presetSize(c.preset).height || 900,
    bg_color: c.bg_color || "",
    wallpaper_url: c.wallpaper_url || "",
    wallpaper_opacity: c.wallpaper_opacity != null ? c.wallpaper_opacity : 0.35,
    fit: c.fit || "fit_width",
    layout_mode: c.layout_mode || (canvas ? "free" : "compact"),
    grid_cols: c.grid_cols || 12,
    locale: c.locale || "",
    language: c.language || "",
    chart_font: c.chart_font || "",
    pivot_engine: c.pivot_engine || "client",
    what_if: c.what_if || [],
    scenarios: c.scenarios || [],
    filter_bar: c.filter_bar || {},
    // Item 97 / 98: tab navigation look + filter panel presentation live on the canvas too
    tabs_bar: c.tabs_bar || {},
    filter_layout: c.filter_layout || "bar",
    filter_panel: c.filter_panel || {},
  };
}

// Effective what-if params: defaults from defs merged with live slider overrides.
// Returns null when no params are defined (nothing is sent to the query engine).
export function effectiveWhatIfParams(defs, values) {
  if (!defs || !defs.length) return null;
  const out = {};
  for (const d of defs) {
    out[d.name] = values && values[d.name] !== undefined ? values[d.name] : (d.default ?? 0);
  }
  return out;
}

export function presetSize(preset) {
  const p = CANVAS_PRESETS.find((x) => x.value === preset);
  return { width: p?.width, height: p?.height };
}

export function isFixedCanvas(canvas) {
  return canvas.preset !== "fluid";
}

// Derive a sensible stacked mobile layout for widgets that don't have one yet.
export function mobileLayoutFor(widgets) {
  const sorted = [...widgets].sort((a, b) => (a.layout?.y || 0) - (b.layout?.y || 0) || (a.layout?.x || 0) - (b.layout?.x || 0));
  const out = {};
  let y = 0;
  for (const w of sorted) {
    if (w.layout_mobile && w.layout_mobile.w != null) {
      out[w.id] = w.layout_mobile;
      y = Math.max(y, (w.layout_mobile.y || 0) + (w.layout_mobile.h || 4));
    } else {
      const h = Math.min(8, Math.max(3, w.layout?.h || 4));
      out[w.id] = { x: 0, y, w: 4, h };
      y += h;
    }
  }
  return out;
}

export function computeFitScale(canvas, containerW, containerH) {
  if (!isFixedCanvas(canvas)) return 1;
  if (canvas.fit === "actual") return 1;
  const sw = containerW / canvas.width;
  if (canvas.fit === "fit_page" && containerH) return Math.min(sw, containerH / canvas.height, 1.5);
  return Math.min(sw, 1.5);
}

// Widget library favorites/recents — cloud-synced (backend) with localStorage cache
// so the panel paints instantly on reload and works offline. Any write updates both.
import api from "./api";

const FAV_KEY = "eip_widget_favs";
const RECENT_KEY = "eip_widget_recent";
export const getFavWidgets = () => { try { return JSON.parse(localStorage.getItem(FAV_KEY)) || []; } catch { return []; } };
export const setFavWidgets = (arr) => {
  const list = Array.isArray(arr) ? arr : [];
  localStorage.setItem(FAV_KEY, JSON.stringify(list));
  api.put("/widget-prefs/favorites", { favorites: list }).catch(() => {});
};
export const getRecentWidgets = () => { try { return JSON.parse(localStorage.getItem(RECENT_KEY)) || []; } catch { return []; } };
export const pushRecentWidget = (type) => {
  const cur = getRecentWidgets().filter((t) => t !== type);
  const next = [type, ...cur].slice(0, 6);
  localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  api.post("/widget-prefs/recent", { type }).catch(() => {});
};
export const syncWidgetPrefs = async () => {
  try {
    const { data } = await api.get("/widget-prefs");
    if (Array.isArray(data?.favorites)) localStorage.setItem(FAV_KEY, JSON.stringify(data.favorites));
    if (Array.isArray(data?.recents)) localStorage.setItem(RECENT_KEY, JSON.stringify(data.recents));
    return { favorites: data.favorites || [], recents: data.recents || [] };
  } catch { return null; }
};
